import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { interpreterAPI, documentsAPI, languagesAPI, bookingsAPI } from '../lib/api';
import { useNotificationEvents } from '../contexts/NotificationContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  Loader2, Radio, DollarSign, Clock, CheckCircle, XCircle,
  PhoneIncoming, Upload, FileText, Plus, Languages, Bell, Filter, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';

const ALL_LANGS = [
  { code: 'es', name: 'Spanish' }, { code: 'zh', name: 'Mandarin' }, { code: 'ar', name: 'Arabic' },
  { code: 'vi', name: 'Vietnamese' }, { code: 'fr', name: 'French' }, { code: 'tl', name: 'Tagalog' },
  { code: 'ko', name: 'Korean' }, { code: 'ru', name: 'Russian' }, { code: 'pt', name: 'Portuguese' },
  { code: 'hi', name: 'Hindi' }
];

const DOC_TYPES = [
  { value: 'police_check', label: 'Police Check' },
  { value: 'w9', label: 'W9 Form' },
  { value: 'certification', label: 'Certification' },
  { value: 'license', label: 'License' },
  { value: 'language_certification', label: 'Language Certification' },
  { value: 'general', label: 'Other Document' },
];

export default function InterpreterDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Approved languages
  const [approvedLangs, setApprovedLangs] = useState([]);
  const [allLanguages, setAllLanguages] = useState([]);

  // Documents
  const [documents, setDocuments] = useState([]);
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [docType, setDocType] = useState('general');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Language requests
  const [langRequests, setLangRequests] = useState([]);
  const [showLangRequest, setShowLangRequest] = useState(false);
  const [newLangCode, setNewLangCode] = useState('');
  const [newLangNotes, setNewLangNotes] = useState('');

  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ description: '', amount: '', notes: '' });

  // Job filters
  const [jobLanguageFilter, setJobLanguageFilter] = useState('');
  const [jobDateFrom, setJobDateFrom] = useState('');
  const [jobDateTo, setJobDateTo] = useState('');

  // Call notification
  const [newJobAlert, setNewJobAlert] = useState(null);
  const [interpreterBookings, setInterpreterBookings] = useState([]);
  const [availableAssignments, setAvailableAssignments] = useState([]);
  const prevJobCount = useRef(0);
  const audioRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [profRes, jobsRes, earnRes, docsRes, langReqRes, invoiceRes, approvedRes, bookRes, availRes] = await Promise.all([
        interpreterAPI.getProfile(),
        interpreterAPI.getJobs(),
        interpreterAPI.getEarnings(),
        interpreterAPI.getDocuments(),
        interpreterAPI.getLanguageRequests(),
        interpreterAPI.getInvoices(),
        interpreterAPI.getApprovedLanguages(),
        bookingsAPI.interpreterList().catch(() => ({ data: [] })),
        bookingsAPI.availableAssignments().catch(() => ({ data: [] })),
      ]);
      setProfile(profRes.data);
      setAvailable(profRes.data.available);
      setJobs(jobsRes.data);
      setEarnings(earnRes.data);
      setDocuments(docsRes.data);
      setLangRequests(langReqRes.data);
      setInvoices(invoiceRes.data);
      setApprovedLangs(approvedRes.data.approved_languages || approvedRes.data.languages || []);
      setInterpreterBookings(bookRes.data || []);
      setAvailableAssignments(availRes.data || []);

      // Check for new jobs - audible notification
      const pendingJobs = jobsRes.data.filter(j => j.status === 'pending');
      if (prevJobCount.current > 0 && pendingJobs.length > prevJobCount.current) {
        setNewJobAlert(pendingJobs[0]);
        try { audioRef.current?.play(); } catch (e) { /* ignore */ }
      }
      prevJobCount.current = pendingJobs.length;
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
    // Load system languages
    languagesAPI.getAll().then(res => {
      const all = [...(res.data.sla || []), ...(res.data.supported || [])];
      setAllLanguages(all);
    }).catch(() => {});
    // Set default date range for job filters
    const today = new Date();
    setJobDateFrom(today.toISOString().split('T')[0]);
    const sixDays = new Date(today);
    sixDays.setDate(sixDays.getDate() + 6);
    setJobDateTo(sixDays.toISOString().split('T')[0]);
  }, [loadData]);

  // Poll for new jobs every 15s
  useEffect(() => {
    const interval = setInterval(() => {
      interpreterAPI.getJobs().then(res => {
        const newPending = res.data.filter(j => j.status === 'pending');
        if (newPending.length > prevJobCount.current && prevJobCount.current >= 0) {
          setNewJobAlert(newPending[0]);
          setJobs(res.data);
          try { audioRef.current?.play(); } catch (e) { /* ignore */ }
        } else {
          setJobs(res.data);
        }
        prevJobCount.current = newPending.length;
      }).catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  // Real-time WebSocket notifications — instant refresh on assignment events
  const { connected: wsConnected } = useNotificationEvents(useCallback((event) => {
    const { type } = event;
    if (['new_assignment', 'assignment_taken', 'assignment_cancelled'].includes(type)) {
      loadData(); // Refresh all data immediately
      if (type === 'new_assignment') {
        toast.info('New assignment available!');
        try { audioRef.current?.play(); } catch (e) { /* ignore */ }
      } else if (type === 'assignment_taken') {
        toast.info('An assignment has been taken by another interpreter');
      }
    }
  }, [loadData]));

  const handleAvailabilityChange = async (val) => {
    setAvailable(val);
    try {
      await interpreterAPI.setAvailability({ available: val, languages: approvedLangs });
    } catch (e) { console.error(e); }
  };

  const handleAcceptJob = async (jobId) => {
    try { await interpreterAPI.acceptJob(jobId); setNewJobAlert(null); await loadData(); } catch (e) { console.error(e); }
  };
  const handleDeclineJob = async (jobId) => {
    try { await interpreterAPI.declineJob(jobId); setNewJobAlert(null); await loadData(); } catch (e) { console.error(e); }
  };

  const handleAcceptBooking = async (bookingId) => {
    try {
      await bookingsAPI.accept(bookingId);
      toast.success('Assignment accepted!');
      await loadData();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(e.response.data.detail || 'This assignment is no longer available.');
      } else {
        toast.error(e.response?.data?.detail || 'Failed to accept');
      }
      await loadData();
    }
  };

  const handleDeclineBooking = async (bookingId) => {
    try {
      await bookingsAPI.decline(bookingId);
      toast.info('Assignment declined');
      await loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to decline'); }
  };

  const handleCompleteBooking = async (bookingId) => {
    try {
      const res = await bookingsAPI.complete(bookingId);
      toast.success(`Assignment complete (${res.data.actual_minutes} min, ${res.data.credits_used} credits)`);
      await loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleHandbackBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to hand back this assignment? This will be tracked in your statistics.')) return;
    try {
      await bookingsAPI.handback(bookingId);
      toast.info('Assignment handed back and re-offered to other interpreters');
      await loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to hand back'); }
  };

  const handleReconfirmBooking = async (bookingId) => {
    try {
      await bookingsAPI.reconfirm(bookingId);
      toast.success('Attendance confirmed!');
      await loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleStartBooking = async (bookingId) => {
    try {
      await bookingsAPI.start(bookingId);
      toast.success('Assignment started');
      await loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Document upload
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);
      await interpreterAPI.uploadDocument(formData);
      toast.success('Document uploaded successfully');
      setShowUploadDoc(false);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Language request
  const handleLangRequest = async () => {
    if (!newLangCode) return;
    try {
      const lang = allLanguages.find(l => l.code === newLangCode);
      await interpreterAPI.createLanguageRequest({ language_code: newLangCode, language_name: lang?.name || newLangCode, notes: newLangNotes });
      toast.success('Language request submitted');
      setShowLangRequest(false);
      setNewLangCode('');
      setNewLangNotes('');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    }
  };

  // Invoice creation
  const handleCreateInvoice = async () => {
    const amountCents = Math.round(parseFloat(invoiceForm.amount) * 100);
    if (!invoiceForm.description || isNaN(amountCents) || amountCents <= 0) {
      toast.error('Please fill in description and a valid amount');
      return;
    }
    try {
      await interpreterAPI.createInvoice({ description: invoiceForm.description, amount_cents: amountCents, notes: invoiceForm.notes });
      toast.success('Invoice submitted');
      setShowCreateInvoice(false);
      setInvoiceForm({ description: '', amount: '', notes: '' });
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    }
  };

  // Filter jobs
  const filteredJobs = jobs.filter(job => {
    if (jobLanguageFilter && job.language !== jobLanguageFilter) return false;
    if (jobDateFrom && job.offered_at < jobDateFrom) return false;
    if (jobDateTo && job.offered_at > jobDateTo + 'T23:59:59') return false;
    return true;
  });

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--brand))]" />
        </div>
      </AppShell>
    );
  }

  const statusBadge = (s) => {
    const map = { pending: 'default', approved: 'secondary', rejected: 'destructive', submitted: 'default', paid: 'secondary' };
    return <Badge variant={map[s] || 'outline'} className="text-xs capitalize">{s}</Badge>;
  };

  return (
    <AppShell>
      {/* Hidden audio for call notification */}
      <audio ref={audioRef} preload="auto" src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbsGczFj2markup=" />

      {/* New Job Alert Banner */}
      {newJobAlert && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-[hsl(var(--brand))] text-white p-3 flex items-center justify-between animate-fade-in" data-testid="new-job-alert">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 animate-pulse" />
            <span className="font-semibold">New Available Assignment!</span>
            <span className="text-sm opacity-90">{ALL_LANGS.find(l => l.code === newJobAlert.language)?.name || newJobAlert.language}</span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => handleDeclineJob(newJobAlert.id)} data-testid="alert-decline-job">Decline</Button>
            <Button size="sm" className="bg-white text-[hsl(var(--brand))] hover:bg-white/90" onClick={() => handleAcceptJob(newJobAlert.id)} data-testid="alert-accept-job">Accept</Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/20" onClick={() => setNewJobAlert(null)}>Dismiss</Button>
          </div>
        </div>
      )}

      <div className="p-6 md:p-10 max-w-6xl mx-auto animate-fade-in" data-testid="interpreter-dashboard">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'DM Sans' }}>
              Interpreter Panel
            </h1>
            <p className="text-muted-foreground mt-1">Manage availability, documents, invoices, and track earnings</p>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
            <div className={`w-3 h-3 rounded-full ${available ? 'bg-[hsl(var(--success))] animate-pulse' : 'bg-muted-foreground/30'}`} />
            <Label className="text-sm font-medium">{available ? 'Online' : 'Offline'}</Label>
            <Switch checked={available} onCheckedChange={handleAvailabilityChange} data-testid="availability-toggle" />
            {wsConnected && (
              <span className="ml-2 flex items-center gap-1 text-[10px] text-green-500" data-testid="ws-connected-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />Live
              </span>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Status', value: profile?.approval_status === 'approved' ? (available ? 'Online' : 'Offline') : 'Pending Approval', icon: Radio, color: available ? 'text-[hsl(var(--success))]' : '' },
            { label: 'Total Sessions', value: earnings?.total_sessions || 0, icon: Clock },
            { label: 'Total Minutes', value: earnings?.total_minutes || 0, icon: PhoneIncoming },
            { label: 'Earnings', value: `$${((earnings?.total_earnings_cents || 0) / 100).toFixed(2)}`, icon: DollarSign },
          ].map((stat, i) => (
            <div key={i} className="rounded-lg border bg-card p-5 transition-all hover:shadow-md" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{stat.label}</p>
                <stat.icon className={`w-4 h-4 ${stat.color || 'text-muted-foreground'}`} />
              </div>
              <p className="text-2xl font-bold mt-2 tracking-tight" style={{ fontFamily: 'DM Sans' }}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-6 h-10">
            <TabsTrigger value="overview" data-testid="tab-overview">Jobs & Languages</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="relative">
              Assignments
              {(availableAssignments.length + interpreterBookings.filter(b => b.status === 'awaiting_confirmation').length) > 0 && (
                <span className="absolute -top-1 -right-1 bg-[hsl(var(--brand))] text-white rounded-full text-[10px] w-4 h-4 flex items-center justify-center">
                  {availableAssignments.length + interpreterBookings.filter(b => b.status === 'awaiting_confirmation').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="lang-requests" data-testid="tab-lang-requests">Language Requests</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
            <TabsTrigger value="earnings" data-testid="tab-earnings">Earnings</TabsTrigger>
          </TabsList>

          {/* ── OVERVIEW TAB ── */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Approved Languages */}
              <div className="rounded-lg border bg-card p-6">
                <h2 className="font-semibold mb-4" style={{ fontFamily: 'DM Sans' }}>My Approved Languages</h2>
                {approvedLangs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No approved languages yet. Request a language below.</p>
                ) : (
                  <div className="space-y-2">
                    {approvedLangs.map(code => {
                      const lang = ALL_LANGS.find(l => l.code === code);
                      return (
                        <div key={code} className="flex items-center gap-2 p-2 rounded-md bg-secondary/50">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm font-medium">{lang?.name || code}</span>
                          <Badge variant="outline" className="text-[10px] font-mono ml-auto">{code.toUpperCase()}</Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
                <Button variant="outline" size="sm" className="mt-4 w-full" onClick={() => setShowLangRequest(true)} data-testid="request-new-language-btn">
                  <Plus className="w-4 h-4 mr-1" />Request New Language
                </Button>
              </div>

              {/* Available Assignments with Filters */}
              <div className="lg:col-span-2 rounded-lg border bg-card">
                <div className="p-5 border-b flex items-center justify-between flex-wrap gap-2">
                  <h2 className="font-semibold" style={{ fontFamily: 'DM Sans' }}>Available Assignments</h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <Select value={jobLanguageFilter} onValueChange={v => setJobLanguageFilter(v === '_all' ? '' : v)}>
                      <SelectTrigger className="w-32 h-8 text-xs" data-testid="job-language-filter">
                        <SelectValue placeholder="All Languages" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">All Languages</SelectItem>
                        {ALL_LANGS.map(l => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input type="date" value={jobDateFrom} onChange={e => setJobDateFrom(e.target.value)} className="w-32 h-8 text-xs" data-testid="job-date-from" />
                    <span className="text-xs text-muted-foreground">to</span>
                    <Input type="date" value={jobDateTo} onChange={e => setJobDateTo(e.target.value)} className="w-32 h-8 text-xs" data-testid="job-date-to" />
                  </div>
                </div>
                {filteredJobs.length === 0 ? (
                  <div className="p-12 text-center text-muted-foreground">
                    <PhoneIncoming className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p>No available assignments matching filters. Stay online to receive requests.</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredJobs.map(job => (
                      <div key={job.id} className="p-4 flex items-center justify-between" data-testid={`job-offer-${job.id}`}>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{ALL_LANGS.find(l => l.code === job.language)?.name || job.language}</p>
                            <Badge variant={job.status === 'pending' ? 'default' : job.status === 'accepted' ? 'secondary' : 'outline'} className="text-xs">{job.status}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground font-mono mt-1">{new Date(job.offered_at).toLocaleString()}</p>
                        </div>
                        {job.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleDeclineJob(job.id)} data-testid={`decline-job-${job.id}`}>
                              <XCircle className="w-4 h-4 mr-1" />Decline
                            </Button>
                            <Button size="sm" onClick={() => handleAcceptJob(job.id)} className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-white" data-testid={`accept-job-${job.id}`}>
                              <CheckCircle className="w-4 h-4 mr-1" />Accept
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>


          {/* ── BOOKINGS TAB ── */}
          <TabsContent value="bookings">
            <div className="space-y-4" data-testid="interpreter-bookings">
              {/* Available Assignments (broadcast model) */}
              {availableAssignments.length > 0 && (
                <div className="rounded-lg border border-[hsl(var(--brand))]/30 bg-[hsl(var(--brand))]/5">
                  <div className="p-4 border-b border-[hsl(var(--brand))]/20">
                    <h3 className="font-semibold text-sm" style={{ fontFamily: 'DM Sans' }}>Available Assignments</h3>
                    <p className="text-xs text-muted-foreground">First interpreter to accept will be given the assignment</p>
                  </div>
                  <div className="divide-y divide-[hsl(var(--brand))]/10">
                    {availableAssignments.map(b => (
                      <div key={b.id} className="p-4 flex items-center justify-between" data-testid={`available-assignment-${b.id}`}>
                        <div className="flex items-center gap-4">
                          <Calendar className="w-5 h-5 text-[hsl(var(--brand))]" />
                          <div>
                            <p className="font-medium text-sm">English &rarr; {ALL_LANGS.find(l => l.code === b.target_language)?.name || b.target_language}</p>
                            <p className="text-xs text-muted-foreground font-mono">{new Date(b.scheduled_at).toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Client: {b.user_name} &middot; {b.duration_minutes} min &middot; {b.domain_level}</p>
                            {b.notes && <p className="text-xs text-muted-foreground/70 mt-1 italic">"{b.notes}"</p>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" className="text-destructive" onClick={() => handleDeclineBooking(b.id)} data-testid={`decline-assignment-${b.id}`}>
                            <XCircle className="w-4 h-4 mr-1" />Decline
                          </Button>
                          <Button size="sm" className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" onClick={() => handleAcceptBooking(b.id)} data-testid={`accept-assignment-${b.id}`}>
                            <CheckCircle className="w-4 h-4 mr-1" />Accept
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Awaiting Confirmation (legacy, for backwards compat) */}
              {interpreterBookings.filter(b => b.status === 'awaiting_confirmation').length > 0 && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5">
                  <div className="p-4 border-b border-amber-500/20">
                    <h3 className="font-semibold text-sm" style={{ fontFamily: 'DM Sans' }}>Action Required</h3>
                    <p className="text-xs text-muted-foreground">These assignments need your confirmation</p>
                  </div>
                  <div className="divide-y divide-amber-500/10">
                    {interpreterBookings.filter(b => b.status === 'awaiting_confirmation').map(b => (
                      <div key={b.id} className="p-4 flex items-center justify-between" data-testid={`pending-booking-${b.id}`}>
                        <div className="flex items-center gap-4">
                          <Calendar className="w-5 h-5 text-amber-500" />
                          <div>
                            <p className="font-medium text-sm">English &rarr; {ALL_LANGS.find(l => l.code === b.target_language)?.name || b.target_language}</p>
                            <p className="text-xs text-muted-foreground font-mono">{new Date(b.scheduled_at).toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Client: {b.user_name} &middot; {b.duration_minutes} min &middot; {b.domain_level}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" className="text-destructive" onClick={() => handleDeclineBooking(b.id)} data-testid={`decline-booking-${b.id}`}>
                            <XCircle className="w-4 h-4 mr-1" />Decline
                          </Button>
                          <Button size="sm" className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" onClick={() => handleAcceptBooking(b.id)} data-testid={`accept-booking-${b.id}`}>
                            <CheckCircle className="w-4 h-4 mr-1" />Accept
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* My Assignments (confirmed, in_progress, completed) */}
              <div className="rounded-lg border bg-card">
                <div className="p-4 border-b">
                  <h3 className="font-semibold text-sm" style={{ fontFamily: 'DM Sans' }}>My Assignments</h3>
                </div>
                <div className="divide-y">
                  {interpreterBookings.filter(b => !['awaiting_confirmation'].includes(b.status)).length === 0 && (
                    <div className="p-8 text-center text-muted-foreground text-sm">No assignments yet</div>
                  )}
                  {interpreterBookings.filter(b => !['awaiting_confirmation'].includes(b.status)).map(b => (
                    <div key={b.id} className="p-4 flex items-center justify-between" data-testid={`booking-row-${b.id}`}>
                      <div className="flex items-center gap-4">
                        <Calendar className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-sm">English &rarr; {ALL_LANGS.find(l => l.code === b.target_language)?.name || b.target_language}</p>
                          <p className="text-xs text-muted-foreground font-mono">{new Date(b.scheduled_at).toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">{b.user_name} &middot; {b.duration_minutes} min</p>
                          {b.actual_minutes && <p className="text-xs text-muted-foreground">Actual: {b.actual_minutes} min &middot; {b.credits_used} credits</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={b.status === 'confirmed' ? 'default' : b.status === 'completed' ? 'secondary' : b.status === 'in_progress' ? 'default' : 'outline'}
                          className={`text-xs capitalize ${b.status === 'confirmed' ? 'bg-green-500/10 text-green-600 border-green-500/20' : b.status === 'in_progress' ? 'bg-blue-500/10 text-blue-600 border-blue-500/20' : ''}`}>
                          {b.status === 'in_progress' ? 'In Progress' : b.status}
                        </Badge>
                        {b.confirmation_sent && !b.reconfirmed && b.status === 'confirmed' && (
                          <Button size="sm" className="text-xs h-7 bg-amber-500 hover:bg-amber-600 text-white" onClick={() => handleReconfirmBooking(b.id)} data-testid={`reconfirm-${b.id}`}>
                            Confirm Attendance
                          </Button>
                        )}
                        {b.reconfirmed && <Badge className="text-xs bg-green-500/10 text-green-600 border-green-500/20">Reconfirmed</Badge>}
                        {b.status === 'confirmed' && (
                          <>
                            <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handleStartBooking(b.id)} data-testid={`start-booking-${b.id}`}>
                              Start
                            </Button>
                            <Button variant="ghost" size="sm" className="text-xs h-7 text-destructive" onClick={() => handleHandbackBooking(b.id)} data-testid={`handback-${b.id}`}>
                              Hand Back
                            </Button>
                          </>
                        )}
                        {b.status === 'in_progress' && (
                          <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handleCompleteBooking(b.id)} data-testid={`complete-booking-${b.id}`}>
                            Mark Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ── DOCUMENTS TAB ── */}
          <TabsContent value="documents">
            <div className="rounded-lg border bg-card">
              <div className="p-5 border-b flex items-center justify-between">
                <h2 className="font-semibold" style={{ fontFamily: 'DM Sans' }}>My Documents</h2>
                <Button size="sm" onClick={() => setShowUploadDoc(true)} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="upload-document-btn">
                  <Upload className="w-4 h-4 mr-1" />Upload Document
                </Button>
              </div>
              {documents.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <FileText className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p>No documents uploaded yet. Upload police checks, W9 forms, and certifications.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>File Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {documents.map(doc => (
                      <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                        <TableCell className="font-medium text-sm">{doc.filename}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs capitalize">{(doc.document_type || 'general').replace(/_/g, ' ')}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(doc.uploaded_at).toLocaleDateString()}</TableCell>
                        <TableCell>{statusBadge(doc.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* ── LANGUAGE REQUESTS TAB ── */}
          <TabsContent value="lang-requests">
            <div className="rounded-lg border bg-card">
              <div className="p-5 border-b flex items-center justify-between">
                <h2 className="font-semibold" style={{ fontFamily: 'DM Sans' }}>Language Approval Requests</h2>
                <Button size="sm" onClick={() => setShowLangRequest(true)} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="request-language-btn">
                  <Languages className="w-4 h-4 mr-1" />Request Language
                </Button>
              </div>
              {langRequests.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <Languages className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p>No language requests yet. Submit a request to add a new language to your profile.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Language</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {langRequests.map(req => (
                      <TableRow key={req.id} data-testid={`lang-req-row-${req.id}`}>
                        <TableCell className="font-medium text-sm">{req.language_name} ({req.language_code})</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{req.notes || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(req.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>{statusBadge(req.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* ── INVOICES TAB ── */}
          <TabsContent value="invoices">
            <div className="rounded-lg border bg-card">
              <div className="p-5 border-b flex items-center justify-between">
                <h2 className="font-semibold" style={{ fontFamily: 'DM Sans' }}>My Invoices</h2>
                <Button size="sm" onClick={() => setShowCreateInvoice(true)} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="create-invoice-btn">
                  <Plus className="w-4 h-4 mr-1" />Create Invoice
                </Button>
              </div>
              {invoices.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p>No invoices yet. Create an invoice to request payment from Interpreter IO.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {invoices.map(inv => (
                      <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                        <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">{inv.description}</TableCell>
                        <TableCell className="font-mono font-semibold">${(inv.amount_cents / 100).toFixed(2)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>{statusBadge(inv.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* ── EARNINGS TAB ── */}
          <TabsContent value="earnings">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-lg border bg-card p-6 text-center">
                <p className="text-sm text-muted-foreground">Total Sessions</p>
                <p className="text-3xl font-bold mt-1" style={{ fontFamily: 'DM Sans' }}>{earnings?.total_sessions || 0}</p>
              </div>
              <div className="rounded-lg border bg-card p-6 text-center">
                <p className="text-sm text-muted-foreground">Total Minutes</p>
                <p className="text-3xl font-bold mt-1" style={{ fontFamily: 'DM Sans' }}>{earnings?.total_minutes || 0}</p>
              </div>
              <div className="rounded-lg border bg-card p-6 text-center">
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-3xl font-bold mt-1" style={{ fontFamily: 'DM Sans' }}>${((earnings?.total_earnings_cents || 0) / 100).toFixed(2)}</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ── UPLOAD DOCUMENT DIALOG ── */}
      <Dialog open={showUploadDoc} onOpenChange={setShowUploadDoc}>
        <DialogContent className="max-w-sm" data-testid="upload-document-dialog">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Upload police checks, W9 forms, certifications, etc.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map(dt => <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>File</Label>
              <Input type="file" ref={fileInputRef} onChange={handleDocUpload} disabled={uploading} data-testid="doc-file-input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
              {uploading && <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Uploading...</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── LANGUAGE REQUEST DIALOG ── */}
      <Dialog open={showLangRequest} onOpenChange={setShowLangRequest}>
        <DialogContent className="max-w-sm" data-testid="language-request-dialog">
          <DialogHeader>
            <DialogTitle>Request New Language</DialogTitle>
            <DialogDescription>Submit a request for admin approval</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Language</Label>
              <Select value={newLangCode} onValueChange={setNewLangCode}>
                <SelectTrigger data-testid="lang-request-select"><SelectValue placeholder="Select language" /></SelectTrigger>
                <SelectContent>
                  {allLanguages.filter(l => !approvedLangs.includes(l.code)).map(l => (
                    <SelectItem key={l.code} value={l.code}>{l.name} ({l.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea value={newLangNotes} onChange={e => setNewLangNotes(e.target.value)} placeholder="Describe your certification or experience..." data-testid="lang-request-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLangRequest(false)}>Cancel</Button>
            <Button onClick={handleLangRequest} disabled={!newLangCode} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-lang-request">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── CREATE INVOICE DIALOG ── */}
      <Dialog open={showCreateInvoice} onOpenChange={setShowCreateInvoice}>
        <DialogContent className="max-w-md" data-testid="create-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>Submit an invoice to Interpreter IO for payment</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input value={invoiceForm.description} onChange={e => setInvoiceForm(p => ({ ...p, description: e.target.value }))} placeholder="Interpretation services for..." data-testid="invoice-description" />
            </div>
            <div className="space-y-2">
              <Label>Amount ($) *</Label>
              <Input type="number" step="0.01" min="0" value={invoiceForm.amount} onChange={e => setInvoiceForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" data-testid="invoice-amount" />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea value={invoiceForm.notes} onChange={e => setInvoiceForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional details..." data-testid="invoice-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateInvoice(false)}>Cancel</Button>
            <Button onClick={handleCreateInvoice} disabled={!invoiceForm.description || !invoiceForm.amount} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-invoice">Submit Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
